<template>
  <div class="seo-page most-played-report">
    <section class="seo-hero">
      <span class="seo-badge">Playtime Report</span>
      <h1>The Most-Played Steam Games of September 2026</h1>
      <p>
        What did Steam players actually spend their time on last month? Not concurrent-player peaks or sales charts, but
        real hours: we looked at the day-by-day playtime of {{ players }} steamplayti.me players across September 2026,
        covering more than 18,000 player-days and over 160,000 hours of play.
      </p>
      <div class="seo-cta-group">
        <router-link class="seo-primary-btn" to="/">Track your own playtime</router-link>
        <a class="seo-secondary-link" href="#top-games">See the top games →</a>
      </div>
      <ul class="seo-highlight-list">
        <li v-for="highlight in highlights" :key="highlight.copy">
          <span class="seo-highlight-icon">{{ highlight.icon }}</span>
          <div>{{ highlight.copy }}</div>
        </li>
      </ul>
    </section>

    <section class="seo-section" id="top-games">
      <h2>Top Games by Number of Players</h2>
      <p>
        Ranked by how many of our {{ players }} players launched the game at least once in September. Counter-Strike 2
        was in a league of its own: more than one in five players put time into it, three times as many as the next game
        on the list. Software that runs through Steam, such as Wallpaper Engine and OBS Studio, is left out of the
        rankings.
      </p>
      <div class="report-table-wrap">
        <table class="report-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Game</th>
              <th>Players</th>
              <th>Share</th>
              <th>Avg. hours / player</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, i) in topGames" :key="row.name">
              <td class="report-table__rank">{{ i + 1 }}</td>
              <td class="report-table__name">{{ row.name }}</td>
              <td>{{ row.players }}</td>
              <td>{{ share(row.players) }}</td>
              <td>{{ row.hoursPerPlayer.toFixed(1) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p>
        The rest of the top 30 is a familiar mix: Helldivers 2, Rainbow Six Siege X, Dead by Daylight, Project Zomboid,
        Apex Legends, Battlefield 6, Balatro, Elden Ring, Rocket League, GTA V Enhanced and Baldur's Gate 3, each played by
        between 34 and 47 of our players.
      </p>
    </section>

    <section class="seo-section">
      <h2>September's Risers</h2>
      <p>
        Comparing each game's share of active players with August shows which games pulled people in during the month.
        Our player base grew in September, so treat these as shifts in what our community played rather than as
        Steam-wide trends.
      </p>
      <div class="seo-grid">
        <div v-for="riser in risers" :key="riser.name" class="seo-card">
          <h3>{{ riser.name }}</h3>
          <p><strong>{{ riser.from }} → {{ riser.to }}</strong> of active players</p>
          <p>{{ riser.note }}</p>
        </div>
      </div>
    </section>

    <section class="seo-section">
      <h2>The Most Dedicated Communities</h2>
      <p>
        Player count is one thing; how long people stay is another. Among games with at least 30 of our players, these
        had the highest average hours per player in September. Survival and live-service shooters dominate: once someone
        starts a Rust wipe, they apparently do not stop.
      </p>
      <div class="seo-grid">
        <div v-for="game in dedicated" :key="game.name" class="seo-card">
          <h3>{{ game.name }}</h3>
          <p><strong>{{ game.hours.toFixed(1) }} hours</strong> per player in September</p>
          <p>{{ game.players }} players</p>
        </div>
      </div>
      <p>
        One curiosity: idle games that sit on the desktop rack up enormous hours from very few people. Bongo Cat logged
        about 2,500 hours from just 22 players, and Task Bar Hero about 1,800 hours from 19. That is why we rank games by
        players rather than by total hours.
      </p>
    </section>

    <section class="seo-section">
      <h2>How Long Does a Steam Player Play per Day?</h2>
      <p>
        On a day they played anything at all, the median steamplayti.me player spent <strong>3 hours 14 minutes</strong>
        in games. The middle half of play days ran from about 1.5 to 6.5 hours. 17% of play days were short sessions under
        an hour, while 42% went past four hours.
      </p>
      <p>
        Weekends matter less than you might expect. The average play day on a Sunday was only about 10% longer than on a
        Monday or Wednesday, and Tuesdays and Thursdays were nearly as busy as Saturdays. For many players, gaming is a
        daily habit rather than a weekend event.
      </p>
      <div class="seo-grid">
        <div v-for="genre in genres" :key="genre.name" class="seo-card">
          <h3>{{ genre.name }}</h3>
          <p><strong>{{ genre.hours }} hours</strong></p>
          <p>played by {{ genre.players }} of our players</p>
        </div>
      </div>
    </section>

    <section class="seo-section">
      <h2>How These Numbers Were Measured</h2>
      <p>
        steamplayti.me records each tracked profile's Steam playtime every night and stores the difference from the day
        before, so every figure above is real time played, as reported by Steam, on a specific day in September 2026.
        A single game can never count more than 24 hours on one day.
      </p>
      <p>
        The sample is the {{ players }} players whose public profiles we tracked and who played something during the
        month. It is a self-selected group of people who care about their playtime, not a random slice of all Steam users,
        so read the rankings as "what our community played." All figures are aggregate; no individual profile is shown or
        identifiable. Genres come from the Steam store, and a game can belong to several.
      </p>
    </section>
    <SeoRelatedTools heading="See Your Own September in Numbers" />
  </div>
</template>

<script>
import { applyMeta, articleStructuredData } from '../utils/meta';
import SeoRelatedTools from '../components/SeoRelatedTools.vue';

// Snapshot of steam_daily_playtime_materialized for 2026-09-01..30, taken
// 2026-10-20. Static on purpose: a monthly report must not drift as the
// inactivity sweep later deletes some of these players' rows.
const PLAYERS = 1205;

const TOP_GAMES = [
  { name: 'Counter-Strike 2', players: 257, hoursPerPlayer: 29.8 },
  { name: 'WARDOGS', players: 84, hoursPerPlayer: 43.4 },
  { name: 'Valheim', players: 81, hoursPerPlayer: 20.2 },
  { name: 'Deadlock', players: 68, hoursPerPlayer: 36.4 },
  { name: 'The Binding of Isaac: Rebirth', players: 63, hoursPerPlayer: 22.5 },
  { name: 'Overwatch 2', players: 61, hoursPerPlayer: 14.0 },
  { name: 'The Witcher 3: Wild Hunt', players: 59, hoursPerPlayer: 2.5 },
  { name: 'Terraria', players: 55, hoursPerPlayer: 9.8 },
  { name: 'Warframe', players: 52, hoursPerPlayer: 43.1 },
  { name: 'Slay the Spire 2', players: 52, hoursPerPlayer: 11.5 },
  { name: 'Geometry Dash', players: 52, hoursPerPlayer: 8.7 },
  { name: 'How to Fish', players: 52, hoursPerPlayer: 3.6 },
];

const RISERS = [
  { name: 'WARDOGS', from: '0%', to: '7.0%', note: 'Absent from our August data, second only to Counter-Strike 2 by players in September.' },
  { name: 'Valheim', from: '0.7%', to: '6.7%', note: 'The biggest comeback of the month, with about 20 hours per player.' },
  { name: 'The Witcher 3', from: '0.8%', to: '4.9%', note: 'Lots of players, short sessions: just 2.5 hours each on average.' },
  { name: 'Aniimo', from: '0%', to: '3.2%', note: 'Released on September 15 and picked up by 38 players in two weeks.' },
  { name: 'Overwatch 2', from: '3.0%', to: '5.1%', note: 'Up by two-thirds as a share of active players.' },
  { name: 'Bodycam', from: '0.7%', to: '2.6%', note: 'The realistic shooter nearly quadrupled its share.' },
];

const DEDICATED = [
  { name: 'Rust', hours: 57.9, players: 33 },
  { name: 'PUBG: Battlegrounds', hours: 47.6, players: 33 },
  { name: 'WARDOGS', hours: 43.4, players: 84 },
  { name: 'Warframe', hours: 43.1, players: 52 },
  { name: 'Dead by Daylight', hours: 40.0, players: 39 },
  { name: 'Deadlock', hours: 36.4, players: 68 },
];

const GENRES = [
  { name: 'Action', hours: '105,300', players: '1,116' },
  { name: 'Free to Play', hours: '55,400', players: '703' },
  { name: 'Adventure', hours: '53,300', players: '922' },
  { name: 'Indie', hours: '52,200', players: '946' },
  { name: 'RPG', hours: '50,100', players: '795' },
  { name: 'Simulation', hours: '36,300', players: '775' },
];

const HIGHLIGHTS = [
  { icon: '🎯', copy: 'Counter-Strike 2 was played by 21% of our players, three times more than any other game.' },
  { icon: '📈', copy: 'WARDOGS and Valheim were the breakout games of the month, each reaching about 7% of players.' },
  { icon: '⏱️', copy: 'The median play day lasted 3 hours 14 minutes, and Sundays were only about 10% longer than Mondays.' },
];

const DESCRIPTION = 'The Steam games our 1,205 tracked players spent the most time on in September 2026: Counter-Strike 2 on top, WARDOGS and Valheim rising, plus daily playtime and genre breakdowns.';
const CANONICAL = 'https://steamplayti.me/blog/most-played-steam-games-september-2026/';

const META = {
  title: 'Most-Played Steam Games of September 2026 | steamplayti.me',
  description: DESCRIPTION,
  keywords: 'most played Steam games, most played Steam games September 2026, Steam playtime statistics, Steam hours per day, top Steam games',
  canonical: CANONICAL,
  ogTitle: 'The Most-Played Steam Games of September 2026',
  ogDescription: 'Real hours from 1,205 Steam players: Counter-Strike 2 dominates, WARDOGS and Valheim surge, and the median play day lasts 3h 14m.',
  ogUrl: CANONICAL,
  twitterTitle: 'Most-Played Steam Games, September 2026',
  twitterDescription: 'Real playtime from 1,205 Steam players: the top games, the risers and how long a play day lasts.',
  structuredData: articleStructuredData({
    headline: 'The Most-Played Steam Games of September 2026',
    description: DESCRIPTION,
    canonical: CANONICAL,
    datePublished: '2026-10-20',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: 'Tools & Blog', path: '/blog/' },
      { name: 'Most-Played Steam Games of September 2026', path: CANONICAL },
    ],
  }),
};

export default {
  name: 'MostPlayedSeptember2026',
  components: { SeoRelatedTools },
  data() {
    return {
      players: PLAYERS.toLocaleString('en-US'),
      topGames: TOP_GAMES,
      risers: RISERS,
      dedicated: DEDICATED,
      genres: GENRES,
      highlights: HIGHLIGHTS,
    };
  },
  mounted() {
    applyMeta(META);
  },
  beforeUnmount() {
    applyMeta();
  },
  methods: {
    share(count) {
      return `${((count / PLAYERS) * 100).toFixed(1)}%`;
    },
  },
};
</script>

<style scoped>
.most-played-report {
  background: radial-gradient(circle at top left, rgba(102, 192, 244, 0.16), transparent 65%);
}

/* Same overflow handling as SteamMachinePrice's price table — see client/CLAUDE.md
   "wide content (tables) overflows the page on mobile". */
.seo-section {
  min-width: 0;
}

.report-table-wrap {
  margin: 1.5rem 0;
  min-width: 0;
  max-width: calc(100vw - 132px);
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.report-table {
  width: 100%;
  min-width: 520px;
  border-collapse: collapse;
  background: var(--surface);
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(102, 192, 244, 0.12);
}

.report-table th,
.report-table td {
  padding: 0.8rem 1.1rem;
  text-align: left;
  border-bottom: 1px solid rgba(102, 192, 244, 0.12);
}

.report-table thead th {
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--primary);
  background: rgba(102, 192, 244, 0.08);
}

.report-table tbody td {
  color: rgba(255, 255, 255, 0.78);
}

.report-table tbody tr:last-child td {
  border-bottom: none;
}

.report-table__rank {
  color: var(--primary);
  font-weight: 700;
}

.report-table__name {
  font-weight: 700;
  color: #fff;
  overflow-wrap: anywhere;
}
</style>
